// Builds the address of a picture at Cloudinary.
//
// Public pages never show an original. They ask Cloudinary for copies at a
// few fixed widths (PUBLIC_WIDTHS), in the best format the browser takes and
// at a quality chosen for photographs, and let the browser pick the one that
// fits the place the picture is shown and the density of the screen. The
// lightbox picks a larger copy from the same set when a picture is opened.
// Migrated images and uploads are treated alike: the address is rebuilt from
// the cloud name, version and public id, whatever the stored address says.
//
// The admin shows only small, heavily compressed copies made for its lists
// and grids (ADMIN_VARIANTS).
//
// Originals are never changed; these are derived copies, made by Cloudinary
// the first time each one is asked for.
import migratedSizes from './imageDimensions.js';

const DEFAULT_CLOUD = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_CLOUDINARY_CLOUD) || 'dfgsj88iu';

// Every public page draws from this one set of widths, in the format the
// browser takes best. Two qualities: 'display' for covers and galleries,
// 'detail' for a picture being looked at on its own (the lightbox and the
// fine-art pages), which costs about twice the bytes for finer texture.
export const PUBLIC_WIDTHS = [640, 960, 1280, 1920, 2400];
export const PUBLIC_QUALITY = { display: 'f_auto,q_auto:good', detail: 'f_auto,q_auto:best' };
const PUBLIC_DEFAULT_WIDTH = 1280; // for a browser that ignores srcset

// Shown at 72x54, up to about 260x195, and up to 320x240 respectively.
export const ADMIN_VARIANTS = {
  row: 'c_fill,w_144,h_108,f_auto,q_auto:low',
  tile: 'c_fill,w_400,h_300,f_auto,q_auto:low',
  cover: 'c_limit,w_640,h_480,f_auto,q_auto:low',
};

// https://res.cloudinary.com/<cloud>/image/upload/[<transformation>/...]v<version>/<public id>[.<ext>]
const DELIVERY_URL = /^https:\/\/res\.cloudinary\.com\/([^/]+)\/image\/upload\/(?:[^/]+\/)*?v(\d+)\/(.+?)(?:\.[A-Za-z0-9]{3,4})?\s*$/;

// The three things that identify a picture, from a reference of either kind.
export function imageParts(ref) {
  if (!ref) return null;
  const fromUrl = typeof ref.legacyUrl === 'string' ? DELIVERY_URL.exec(ref.legacyUrl.trim()) : null;
  if (typeof ref.publicId === 'string' && Number.isInteger(ref.version)) {
    return { cloud: ref.cloud || (fromUrl && fromUrl[1]) || DEFAULT_CLOUD, version: ref.version, publicId: ref.publicId };
  }
  return fromUrl ? { cloud: fromUrl[1], version: Number(fromUrl[2]), publicId: fromUrl[3] } : null;
}

const derived = (parts, transformation) =>
  `https://res.cloudinary.com/${parts.cloud}/image/upload/${transformation}/v${parts.version}/${parts.publicId}`;

// The size of the original as displayed, [width, height], or null. Uploads
// carry it; migrated images are listed in imageDimensions.json.
export function imageSize(ref) {
  if (!ref) return null;
  if (ref.width > 0 && ref.height > 0) return [ref.width, ref.height];
  const parts = imageParts(ref);
  return (parts && migratedSizes[parts.publicId]) || null;
}

// The size to lay a picture out with once it has loaded. Normally the known
// size. If the picture turns out to be upright where the known size lies on
// its side (a photograph stored sideways with a camera flag, recorded before
// uploads noted the displayed size), the known size the other way round.
export function sizeAsLoaded(size, naturalWidth, naturalHeight) {
  if (!size || !(naturalWidth > 0) || !(naturalHeight > 0)) return size;
  if (size[0] === size[1] || naturalWidth === naturalHeight) return size;
  const lying = size[0] > size[1];
  const loadedLying = naturalWidth > naturalHeight;
  return lying === loadedLying ? size : [size[1], size[0]];
}

// The copies worth offering: the fixed widths below the original's, then one
// at the original's own width (or the largest fixed width, whichever is less).
// c_limit never enlarges, so each entry says how wide its copy really is.
function candidates(parts, size, quality) {
  const setting = PUBLIC_QUALITY[quality] || PUBLIC_QUALITY.display;
  const entry = (width, actual = width) => ({ width: actual, url: derived(parts, `c_limit,w_${width},${setting}`) });
  const largest = PUBLIC_WIDTHS[PUBLIC_WIDTHS.length - 1];
  if (!size || size[0] >= largest) return PUBLIC_WIDTHS.map((width) => entry(width));
  const top = PUBLIC_WIDTHS.find((width) => width >= size[0]);
  return [...PUBLIC_WIDTHS.filter((width) => width < size[0]).map((width) => entry(width)), entry(top, size[0])];
}

// What an <img> on a public page needs: { src, srcSet, width, height }.
// width and height are the original's, so the browser reserves the right
// space and lays the picture out exactly as it would the original.
export function publicImage(ref, quality = 'display') {
  if (!ref) return null;
  const parts = imageParts(ref);
  // An address that cannot be understood is shown as it is.
  if (!parts) return ref.legacyUrl ? { src: ref.legacyUrl } : null;
  const size = imageSize(ref);
  const list = candidates(parts, size, quality);
  const fallback = list.find((entry) => entry.width >= PUBLIC_DEFAULT_WIDTH) || list[list.length - 1];
  return {
    src: fallback.url,
    srcSet: list.map((entry) => `${entry.url} ${entry.width}w`).join(', '),
    ...(size ? { width: size[0], height: size[1] } : {}),
  };
}

// For the lightbox: the smallest detail copy that is at least `neededWidth`
// real pixels wide, from the same set of widths.
export function publicImageAtLeast(ref, neededWidth) {
  if (!ref) return '';
  const parts = imageParts(ref);
  if (!parts) return ref.legacyUrl || '';
  const list = candidates(parts, imageSize(ref), 'detail');
  return (list.find((entry) => entry.width >= neededWidth) || list[list.length - 1]).url;
}

// For the admin: 'row', 'tile' or 'cover'.
export function adminImageUrl(ref, variant) {
  const transformation = ADMIN_VARIANTS[variant];
  if (!ref || !transformation) return '';
  const parts = imageParts(ref);
  // An address that cannot be understood is shown as it is rather than not at all.
  return parts ? derived(parts, transformation) : ref.legacyUrl || '';
}
